import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SECTIONS } from "@/lib/site";
import { PageHeader } from "@/components/Doodles";
import { ImageTile, Tile } from "@/components/ImageTile";
import { LibraryPicker } from "@/components/LibraryPicker";
import { BulkUpload } from "@/components/BulkUpload";

interface Row { slug: string; title: string; section: string; status: string; design: string | null; hero: Tile; projects: ({ index: number; title: string } & Tile)[] }
type Target = { slug: string; index: number | null };
const KINDS = [["all", "All"], ["ai", "Example (AI)"], ["mine", "My photos"], ["missing", "Missing"]] as const;

const match = (t: Tile, kind: string) => kind === "all" || (kind === "missing" ? !t.image : kind === "ai" ? !!t.image && t.image_ai : !!t.image && !t.image_ai);

export default function ImagesPage() {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [section, setSection] = useState("");
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [target, setTarget] = useState<Target | null>(null);
  const load = useCallback(() => { api.get("/admin/images").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => (rows || []).filter((r) => (!section || r.section === section) && r.title.toLowerCase().includes(q.toLowerCase())
    && (match(r.hero, kind) || r.projects.some((p) => match(p, kind)))), [rows, section, kind, q]);
  const pick = async (url: string) => {
    if (!target) return;
    try { await api.post("/admin/images/assign", { ...target, url }); toast.success("Image swapped"); setTarget(null); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="images-page">
      <PageHeader title="Images" kicker="Every article and project photo · swap any of them" />
      {rows && <BulkUpload rows={rows} onDone={load} />}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <select className="rounded-full border-2 border-[#23324A]/30 bg-white px-3 py-2 text-sm" value={section} onChange={(e) => setSection(e.target.value)} data-testid="images-section-filter">
          <option value="">All sections</option>
          {SECTIONS.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
        </select>
        {KINDS.map(([k, l]) => <button key={k} className={`chip ${kind === k ? "chip-active" : ""}`} onClick={() => setKind(k)} data-testid={`images-kind-${k}`}>{l}</button>)}
        <input className="ml-auto w-full rounded-full border-2 border-[#23324A]/30 bg-white px-4 py-2 text-sm sm:w-64" placeholder="Search articles" value={q} onChange={(e) => setQ(e.target.value)} data-testid="images-search" />
      </div>
      {!rows && <p className="text-sm text-[#23324A]/60">Loading…</p>}
      {rows && <p className="mb-4 text-xs text-[#23324A]/60" data-testid="images-count">{shown.length} {shown.length === 1 ? "article" : "articles"}</p>}
      <div className="space-y-6">
        {shown.map((r) => (
          <section key={r.slug} className="rounded-2xl border-2 border-dashed border-[#23324A]/25 bg-[#FDFBF7] p-4" data-testid={`images-article-${r.slug}`}>
            <div className="mb-3 flex flex-wrap items-baseline gap-2">
              <h2 className="font-display text-base font-extrabold text-[#23324A] md:text-lg">{r.title}</h2>
              <span className="text-xs text-[#23324A]/60">{SECTIONS.find((s) => s.slug === r.section)?.name || "No section"}{r.status === "planned" ? " · hidden" : ""}</span>
              <a href={`/${r.slug}`} target="_blank" rel="noreferrer" className="ml-auto text-xs font-semibold text-[#D9785C] hover:underline" data-testid={`images-view-${r.slug}`}>View article</a>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <ImageTile slug={r.slug} index={null} label="Main article photo" tile={r.hero} onPick={() => setTarget({ slug: r.slug, index: null })} onDone={load} />
              {r.projects.map((p) => <ImageTile key={p.index} slug={r.slug} index={p.index} label={p.title} tile={p} onPick={() => setTarget({ slug: r.slug, index: p.index })} onDone={load} />)}
            </div>
          </section>
        ))}
      </div>
      <LibraryPicker open={!!target} onClose={() => setTarget(null)} onPick={pick} />
    </div>
  );
}
